import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  List, 
  Card, 
  Tag, 
  Pagination, 
  Input, 
  Empty,
  Spin,
  message,
  Button,
  Tooltip
} from 'antd';
import { 
  ArrowLeftOutlined, 
  LockOutlined, 
  UnlockOutlined,
  CalendarOutlined,
  TagOutlined
} from '@ant-design/icons';
import { blogApi } from '../utils/api';
import './BlogList.css';

const { Search } = Input;

const BlogList = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchKeyword, setSearchKeyword] = useState('');
  const navigate = useNavigate();

  // 获取博客列表
  const fetchBlogs = async (page = 1, pageSize = 10, keyword = '') => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
      };
      if (keyword) {
        params.keyword = keyword;
      }
      
      const response = await blogApi.getList(params);
      if (response.code === 200) {
        setBlogs(response.data.items);
        setPagination({
          current: response.data.page,
          pageSize: response.data.page_size,
          total: response.data.total,
        });
      }
    } catch (error) {
      message.error('获取博客列表失败：' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  // 分页变化
  const handlePageChange = (page, pageSize) => {
    fetchBlogs(page, pageSize, searchKeyword);
  };

  // 搜索
  const handleSearch = (value) => {
    setSearchKeyword(value);
    fetchBlogs(1, pagination.pageSize, value);
  };

  // 跳转到博客详情
  const handleBlogClick = (uuid) => {
    navigate(`/blog/${uuid}`);
  };

  // 返回首页
  const handleBack = () => {
    navigate('/');
  };

  return (
    <div className="blog-list-container">
      {/* 顶部导航 */}
      <div className="blog-list-header">
        <Tooltip title="返回首页">
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleBack}
            className="back-btn"
          >
            返回
          </Button>
        </Tooltip>
        <h1 className="blog-list-title">博客列表</h1>
        <div className="header-placeholder"></div>
      </div>

      {/* 搜索栏 */}
      <div className="blog-list-search">
        <Search
          placeholder="搜索博客标题或内容..."
          allowClear
          enterButton="搜索"
          size="large"
          onSearch={handleSearch}
          className="search-input"
        />
      </div>

      {/* 博客列表 */}
      <div className="blog-list-content">
        <Spin spinning={loading} size="large">
          {blogs.length > 0 ? (
            <List
              grid={{ gutter: 24, xs: 1, sm: 1, md: 2, lg: 2, xl: 3, xxl: 3 }}
              dataSource={blogs}
              renderItem={(blog) => (
                <List.Item>
                  <Card
                    hoverable
                    className="blog-card"
                    onClick={() => handleBlogClick(blog.uuid)}
                  >
                    <div className="blog-card-header">
                      <h3 className="blog-card-title">{blog.title}</h3>
                      {blog.is_private ? (
                        <LockOutlined className="privacy-icon private" />
                      ) : (
                        <UnlockOutlined className="privacy-icon public" />
                      )}
                    </div>
                    
                    <div className="blog-card-meta">
                      <span className="meta-item">
                        <CalendarOutlined />
                        {new Date(blog.created_at).toLocaleDateString('zh-CN')}
                      </span>
                    </div>

                    {blog.tags && blog.tags.length > 0 && (
                      <div className="blog-card-tags">
                        <TagOutlined className="tags-icon" />
                        {blog.tags.map((tag, index) => (
                          <Tag key={index} className="blog-tag">{tag}</Tag>
                        ))}
                      </div>
                    )}
                  </Card>
                </List.Item>
              )}
            />
          ) : (
            <Empty 
              description="暂无博客文章" 
              className="empty-state"
            />
          )}
        </Spin>
      </div>

      {/* 分页 */}
      {blogs.length > 0 && (
        <div className="blog-list-pagination">
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={handlePageChange}
            showSizeChanger
            showQuickJumper
            showTotal={(total) => `共 ${total} 篇博客`}
          />
        </div>
      )}
    </div>
  );
};

export default BlogList;
